import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import {
  composeAgencyTodayStartHere,
  type CanonicalPresentationModelV1,
} from "@enrein/shared-contracts";
import { createStudioApiClient, type Step5RoomTruthV1, type StudioApiClient } from "../../api.js";
import { readStudioRuntimeConfig } from "../../env.js";
import { usePolling } from "../../usePolling.js";
import { WebsiteOwnershipRecordPanel } from "../../WebsiteOwnershipRecordPanel.js";
import { GreenfieldShell } from "./chrome/GreenfieldShell.js";
import {
  confirmCandidateOnce,
  createClientAccessOnce,
  markChangeRequestInProgressOnce,
  packageHandoffOnce,
  prepareDeliveryOnce,
  resolveChangeRequestWithLatestPreviewOnce,
  saveBusinessDescriptionDraftOnce,
  startConfirmedBuildOnce,
} from "./greenfield-material-actions.js";
import { loadGreenfieldProjectPreview, workbenchPreviewNotice } from "./greenfield-preview.js";
import {
  ChangeDiff,
  Condition,
  ExactVersionChip,
  FailureGrammar,
  GlobalNotice,
  StartHere,
  Table,
  Toast,
} from "./components/WorkspacePrimitives.js";
import {
  approvalCopyFromRecord,
  approvalPreviewVersionId,
  changesEmptyCopy,
  classifyGreenfieldListLoad,
  clientAccessLabel,
  clientLinkActionLabel,
  composeTodayInputFromPipeline,
  candidateConfirmationDescription,
  currentDraftDescriptionPrefill,
  draftNeedsReconfirmation,
  greenfieldTruthPollMs,
  isFilterableGreenfieldView,
  presentGreenfieldListRow,
  projectOrientation,
  shouldOfferCreateClientLink,
  shouldOfferPackageHandoff,
  shouldOfferPrepareDelivery,
  shouldOfferReissueClientLink,
  versionTruthFromRecords,
  type PipelineTruthItem,
} from "./greenfield-truth.js";
import {
  buildGreenfieldSearch,
  parseGreenfieldRoute,
  projectRoomTabLabel,
  projectRoomTabs,
  type GreenfieldNavId,
  type GreenfieldRouteState,
} from "./routing/greenfield-routing.js";

function replaceRoute(next: GreenfieldRouteState): void {
  const search = buildGreenfieldSearch(next);
  window.history.replaceState(next, "", `${window.location.pathname}${search}`);
}

function newIdempotencyKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export function EnreinGreenfieldApp({
  agencyId,
  onLogout,
}: {
  readonly agencyId: string;
  readonly onLogout?: () => Promise<void> | void;
}) {
  const config = useMemo(() => readStudioRuntimeConfig(), []);
  const api = useMemo(
    () => createStudioApiClient(config, { agencyId }),
    [agencyId, config],
  );
  const [route, setRoute] = useState<GreenfieldRouteState>(() =>
    parseGreenfieldRoute(window.location.search),
  );
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [todayModel, setTodayModel] = useState<CanonicalPresentationModelV1 | null>(null);
  const [businesses, setBusinesses] = useState<
    Array<{ businessId: string; displayName: string; projectIds: string[] }>
  >([]);
  const [projects, setProjects] = useState<PipelineTruthItem[]>([]);
  const [freshLinkUrl, setFreshLinkUrl] = useState<string | null>(null);
  const linkCreateInFlight = useRef(false);
  const confirmInFlight = useRef(false);
  const buildInFlight = useRef(false);
  const changeInFlight = useRef(false);
  const deliveryInFlight = useRef(false);

  const navigate = useCallback((next: GreenfieldRouteState) => {
    setRoute(next);
    setToast(null);
    replaceRoute(next);
  }, []);

  const reloadLists = useCallback(async () => {
    const [today, pipeline, businessList] = await Promise.allSettled([
      api.getStudioToday(),
      api.getStudioProjectPipeline(),
      api.listStudioBusinesses(),
    ]);
    const pipelineFailed = pipeline.status === "rejected";
    const todayFailed = today.status === "rejected";
    const businessesFailed = businessList.status === "rejected";
    const load = classifyGreenfieldListLoad({ pipelineFailed, todayFailed, businessesFailed });
    if (load.unavailable) {
      setError("Studio data is unavailable. Retry from this page.");
      setTodayModel(null);
      setListsLoaded(false);
      return;
    }
    setError(null);
    if (pipeline.status === "fulfilled") {
      setProjects([...(pipeline.value.items ?? [])]);
      const startHere = composeAgencyTodayStartHere(
        (pipeline.value.items ?? []).map((item) => composeTodayInputFromPipeline(item)),
      );
      setTodayModel(startHere?.model ?? null);
    } else {
      setTodayModel(null);
    }
    if (businessList.status === "fulfilled") {
      setBusinesses(businessList.value.items);
    }
    setListsLoaded(load.listsLoaded);
  }, [api]);

  useEffect(() => {
    const onPop = () => setRoute(parseGreenfieldRoute(window.location.search));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await reloadLists();
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : "studio_load_failed");
          setTodayModel(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadLists]);

  const onNav = (nav: GreenfieldNavId) => {
    if (nav === "today") navigate({ view: "today", projectId: null, businessId: null, section: null });
    if (nav === "businesses") navigate({ view: "businesses", projectId: null, businessId: null, section: null });
    if (nav === "work") navigate({ view: "work", projectId: null, businessId: null, section: null });
    if (nav === "settings") navigate({ view: "settings", projectId: null, businessId: null, section: null });
  };

  const filteredBusinesses = businesses.filter((row) =>
    row.displayName.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const filteredProjects = projects.filter((row) =>
    `${row.businessName ?? ""} ${row.projectId}`.toLowerCase().includes(query.trim().toLowerCase()),
  );

  const openProject = (projectId: string, businessId: string | null, section: GreenfieldRouteState["section"]) =>
    navigate({ view: "project", projectId, businessId, section: section ?? "overview" });

  return (
    <GreenfieldShell
      view={route.view}
      query={query}
      onQuery={setQuery}
      onNavigate={onNav}
      showFilter={isFilterableGreenfieldView(route.view, Boolean(error))}
    >
      <Toast message={toast} />
      {error ? (
        <FailureGrammar title="Something needs a retry" detail={error} onRetry={() => void reloadLists()} />
      ) : null}
      {!error && route.view === "today" ? (
        <TodayView
          model={todayModel}
          listsLoaded={listsLoaded}
          onOpen={() => {
            const action = todayModel?.primaryAction;
            if (!action || !("projectId" in action) || !action.projectId) {
              navigate({ view: "work", projectId: null, businessId: null, section: null });
              return;
            }
            const projectId = action.projectId;
            const task = "task" in action ? action.task : null;
            if (action.intent === "START_PROJECT_BUILD") {
              void (async () => {
                const result = await startConfirmedBuildOnce({
                  api,
                  projectId,
                  inFlight: buildInFlight,
                });
                if (!result.ok) setToast(result.error);
                await reloadLists();
                openProject(projectId, null, "website");
              })();
              return;
            }
            if (task === "REVIEW_BUSINESS_INFORMATION") {
              openProject(projectId, null, "business");
              return;
            }
            if (task === "OPEN_CLIENT_LINK") {
              openProject(projectId, null, "overview");
              return;
            }
            openProject(projectId, null, "overview");
          }}
        />
      ) : null}
      {route.view === "businesses" ? (
        <BusinessesView
          rows={filteredBusinesses}
          onOpen={(businessId) =>
            navigate({ view: "business", businessId, projectId: null, section: null })
          }
          onPortfolio={() => navigate({ view: "portfolio", businessId: null, projectId: null, section: null })}
        />
      ) : null}
      {route.view === "business" && route.businessId ? (
        <BusinessDetailView
          businessId={route.businessId}
          projects={projects}
          api={api}
          onOpenProject={(projectId) => openProject(projectId, route.businessId, "overview")}
        />
      ) : null}
      {route.view === "portfolio" ? <PortfolioView rows={filteredProjects} /> : null}
      {route.view === "work" ? (
        <WorkView
          rows={filteredProjects}
          onCreate={() => navigate({ view: "create", projectId: null, businessId: null, section: null })}
          onOpen={(projectId) => openProject(projectId, null, "overview")}
        />
      ) : null}
      {route.view === "create" ? (
        <CreateView
          api={api}
          businesses={businesses}
          onCreated={async (projectId, businessId) => {
            setToast("Project created. Client access and build were not started.");
            await reloadLists();
            navigate({ view: "project", projectId, businessId, section: "overview" });
          }}
        />
      ) : null}
      {route.view === "project" && route.projectId ? (
        <ProjectRoomView
          projectId={route.projectId}
          section={route.section ?? "overview"}
          api={api}
          config={config}
          agencyId={agencyId}
          pipeline={projects}
          freshLinkUrl={freshLinkUrl}
          onSection={(section) => navigate({ ...route, view: "project", section })}
          onWorkbench={() =>
            navigate({ view: "workbench", projectId: route.projectId, businessId: route.businessId, section: null })
          }
          onToast={setToast}
          onReloadLists={() => void reloadLists()}
          onFreshLink={setFreshLinkUrl}
          linkCreateInFlight={linkCreateInFlight}
          confirmInFlight={confirmInFlight}
          buildInFlight={buildInFlight}
          changeInFlight={changeInFlight}
          deliveryInFlight={deliveryInFlight}
        />
      ) : null}
      {route.view === "workbench" && route.projectId ? (
        <WorkbenchView
          api={api}
          projectId={route.projectId}
          pipeline={projects}
          pollIntervalMs={config.pollIntervalMs}
          onBack={() =>
            navigate({ view: "project", projectId: route.projectId, businessId: route.businessId, section: "website" })
          }
        />
      ) : null}
      {route.view === "settings" ? <SettingsView onLogout={onLogout} /> : null}
    </GreenfieldShell>
  );
}

function TodayView({
  model,
  listsLoaded,
  onOpen,
}: {
  readonly model: CanonicalPresentationModelV1 | null;
  readonly listsLoaded: boolean;
  readonly onOpen: () => void;
}) {
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <h1 className="ws-display">Today</h1>
      {model ? (
        <StartHere model={model} onPrimary={onOpen} />
      ) : listsLoaded ? (
        <GlobalNotice>You're clear. No Delivery work is waiting.</GlobalNotice>
      ) : (
        <p className="ws-muted">Loading Studio work…</p>
      )}
    </div>
  );
}

function BusinessesView({
  rows,
  onOpen,
  onPortfolio,
}: {
  readonly rows: ReadonlyArray<{ businessId: string; displayName: string; projectIds: string[] }>;
  readonly onOpen: (businessId: string) => void;
  readonly onPortfolio: () => void;
}) {
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <h1 className="ws-display">Businesses</h1>
      <p className="ws-muted">Grouped by business identity, never by matching names.</p>
      <button type="button" className="ws-btn" onClick={onPortfolio}>
        Open portfolio
      </button>
      <Table
        columns={["Business", "Projects", "Action"]}
        empty="No businesses yet."
        rows={rows.map((row) => [
          row.displayName,
          String(row.projectIds.length),
          <button key={row.businessId} type="button" className="ws-btn" onClick={() => onOpen(row.businessId)}>
            Open
          </button>,
        ])}
      />
    </div>
  );
}

function BusinessDetailView({
  businessId,
  projects,
  api,
  onOpenProject,
}: {
  readonly businessId: string;
  readonly projects: ReadonlyArray<PipelineTruthItem>;
  readonly api: StudioApiClient;
  readonly onOpenProject: (projectId: string) => void;
}) {
  const [name, setName] = useState("");
  const [projectIds, setProjectIds] = useState<string[]>([]);
  useEffect(() => {
    void api.getStudioBusiness(businessId).then((row) => {
      setName(row.displayName);
      setProjectIds(row.projectIds);
    }).catch(() => {
      setName("");
    });
  }, [api, businessId]);
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <h1 className="ws-display">{name || "Business"}</h1>
      <p className="ws-muted">This business remains after projects complete or archive.</p>
      <Table
        columns={["Project", "Status", "Stage", "Action"]}
        empty="No projects bound to this business."
        rows={projectIds.map((projectId) => {
          const match = projects.find((row) => row.projectId === projectId);
          const presented = presentGreenfieldListRow(
            match ?? { projectId, businessName: name || "Website project" },
          );
          return [
            match?.businessName || name || "Website project",
            presented.statusLabel,
            presented.stageLabel,
            <button key={projectId} type="button" className="ws-btn" onClick={() => onOpenProject(projectId)}>
              Open project
            </button>,
          ];
        })}
      />
    </div>
  );
}

function PortfolioView({
  rows,
}: {
  readonly rows: ReadonlyArray<PipelineTruthItem>;
}) {
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <h1 className="ws-display">Portfolio</h1>
      <Table
        columns={["Business", "Status"]}
        empty="No source-backed portfolio rows."
        rows={rows.map((row) => [
          row.businessName ?? "Unnamed business",
          presentGreenfieldListRow(row).statusLabel,
        ])}
      />
    </div>
  );
}

function WorkView({
  rows,
  onCreate,
  onOpen,
}: {
  readonly rows: ReadonlyArray<PipelineTruthItem>;
  readonly onCreate: () => void;
  readonly onOpen: (projectId: string) => void;
}) {
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <h1 className="ws-display">Work</h1>
      <button type="button" className="ws-btn ws-btn-primary" onClick={onCreate}>
        Create business and project
      </button>
      <Table
        columns={["Business", "Stage", "Action"]}
        empty="No projects in the pipeline."
        rows={rows.map((row) => {
          const presented = presentGreenfieldListRow(row);
          return [
            row.businessName ?? "Unnamed business",
            presented.stageLabel,
            <button key={row.projectId} type="button" className="ws-btn" onClick={() => onOpen(row.projectId)}>
              Open
            </button>,
          ];
        })}
      />
    </div>
  );
}

function CreateView({
  api,
  businesses,
  onCreated,
}: {
  readonly api: StudioApiClient;
  readonly businesses: ReadonlyArray<{ businessId: string; displayName: string }>;
  readonly onCreated: (projectId: string, businessId: string | null) => Promise<void> | void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [businessId, setBusinessId] = useState("");
  const [ownerLabel, setOwnerLabel] = useState("Agency owner");
  const [sitePackageId, setSitePackageId] = useState<"package-one-page" | "package-basic-3" | "package-standard-5">("package-one-page");
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState<string | null>(null);
  return (
    <form
      style={{ display: "grid", gap: 16, maxWidth: 560 }}
      onSubmit={async (event) => {
        event.preventDefault();
        if (busy) return;
        setBusy(true);
        setDenied(null);
        try {
          const created = await api.createProject({
            businessName: displayName,
            clientEntityName: displayName,
            scopeSummary: `Initial scope for ${displayName}`,
            ownerLabel,
            builderPath: "ENREIN_GENERATE",
            sitePackageId,
            targetOutcome: "Launch an approved client website revision",
            businessId: businessId || undefined,
          });
          await onCreated(created.projectId, created.businessId ?? (businessId || null));
        } catch (error) {
          setDenied(error instanceof Error ? error.message : "create_denied");
        } finally {
          setBusy(false);
        }
      }}
    >
      <h1 className="ws-display">Create business and project</h1>
      <div className="ws-field">
        <label htmlFor="create-business-name">Business display name</label>
        <input
          id="create-business-name"
          required
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </div>
      <div className="ws-field">
        <label htmlFor="create-existing-business">Attach existing business</label>
        <select
          id="create-existing-business"
          value={businessId}
          onChange={(event) => setBusinessId(event.target.value)}
        >
          <option value="">Create a new business</option>
          {businesses.map((row) => (
            <option key={row.businessId} value={row.businessId}>
              {row.displayName}
            </option>
          ))}
        </select>
      </div>
      <div className="ws-field">
        <label htmlFor="create-site-package">Website package</label>
        <select
          id="create-site-package"
          value={sitePackageId}
          onChange={(event) => setSitePackageId(event.target.value as typeof sitePackageId)}
        >
          <option value="package-one-page">One page</option>
          <option value="package-basic-3">Basic — 3 pages</option>
          <option value="package-standard-5">Standard — 5 pages</option>
        </select>
      </div>
      <div className="ws-field">
        <label htmlFor="create-owner">Owner</label>
        <input id="create-owner" value={ownerLabel} onChange={(event) => setOwnerLabel(event.target.value)} />
      </div>
      {denied ? <FailureGrammar title="Create was denied" detail={denied} /> : null}
      <button type="submit" className="ws-btn ws-btn-primary" disabled={busy}>
        {busy ? "Creating" : "Create"}
      </button>
    </form>
  );
}

function ProjectRoomView({
  projectId,
  section,
  api,
  config,
  agencyId,
  pipeline,
  freshLinkUrl,
  onSection,
  onWorkbench,
  onToast,
  onReloadLists,
  onFreshLink,
  linkCreateInFlight,
  confirmInFlight,
  buildInFlight,
  changeInFlight,
  deliveryInFlight,
}: {
  readonly projectId: string;
  readonly section: string;
  readonly api: StudioApiClient;
  readonly config: ReturnType<typeof readStudioRuntimeConfig>;
  readonly agencyId: string;
  readonly pipeline: ReadonlyArray<{ projectId: string; businessName?: string | null }>;
  readonly freshLinkUrl: string | null;
  readonly onSection: (section: GreenfieldRouteState["section"]) => void;
  readonly onWorkbench: () => void;
  readonly onToast: (message: string | null) => void;
  readonly onReloadLists: () => void;
  readonly onFreshLink: (url: string | null) => void;
  readonly linkCreateInFlight: MutableRefObject<boolean>;
  readonly confirmInFlight: MutableRefObject<boolean>;
  readonly buildInFlight: MutableRefObject<boolean>;
  readonly changeInFlight: MutableRefObject<boolean>;
  readonly deliveryInFlight: MutableRefObject<boolean>;
}) {
  const [truth, setTruth] = useState<Step5RoomTruthV1 | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [inlinePreviewHtml, setInlinePreviewHtml] = useState<string | null>(null);
  const [inlinePreviewRunId, setInlinePreviewRunId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [notes, setNotes] = useState<Array<{ noteId?: string; body?: string; noteType?: string }>>([]);
  const [noteBody, setNoteBody] = useState("");
  const [activity, setActivity] = useState<Array<{ eventType?: string; occurredAt?: string; payloadSummary?: string }>>([]);
  const [departureKit, setDepartureKit] = useState<{ downloadAvailable: boolean } | null>(null);
  const [businessDescription, setBusinessDescription] = useState("");
  const [selectedLensId, setSelectedLensId] = useState<"lens-flat-2" | "lens-glass">("lens-flat-2");
  useEffect(() => { setSelectedLensId("lens-flat-2"); }, [projectId]);
  const draftDescriptionSeed = truth?.draft
    ? `${truth.draft.draftId}:${truth.draft.draftVersion}:${truth.draft.description ?? ""}`
    : "";

  const loadTruth = useCallback(() => api.getStep5RoomTruth(projectId), [api, projectId]);
  const pollMs = greenfieldTruthPollMs(truth, config.pollIntervalMs);
  const polled = usePolling(loadTruth, pollMs);

  const reloadTruth = useCallback(async () => {
    const view = await api.getStep5RoomTruth(projectId);
    setTruth(view);
    setLoadError(null);
    return view;
  }, [api, projectId]);

  useEffect(() => {
    if (polled.data) {
      setTruth(polled.data);
      setLoadError(null);
    } else if (polled.error) {
      setLoadError(polled.error);
    }
  }, [polled.data, polled.error]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") polled.refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [polled.refresh]);

  useEffect(() => {
    setBusinessDescription(
      currentDraftDescriptionPrefill({
        draftDescription: truth?.draft?.description,
        changeRequestMessage: truth?.changeRequest?.message,
      }),
    );
  }, [draftDescriptionSeed, truth?.draft?.description, truth?.changeRequest?.message]);

  useEffect(() => {
    if (section !== "notes") return;
    void api.listProjectNotes(projectId).then((row) => setNotes([...(row.notes ?? [])])).catch(() => setNotes([]));
  }, [api, projectId, section]);

  useEffect(() => {
    if (section !== "activity") return;
    void api.listProjectActivity(projectId).then((row) => setActivity([...(row.items ?? [])])).catch(() => setActivity([]));
  }, [api, projectId, section]);

  useEffect(() => {
    if (section !== "approval-delivery" || !truth?.approval?.approvalId || !truth.deliveryDueAt) {
      setDepartureKit(null);
      return;
    }
    let cancelled = false;
    const refreshKit = () => {
      void api
        .getDepartureKitStatus(projectId)
        .then((row) => {
          if (!cancelled) setDepartureKit({ downloadAvailable: Boolean(row.downloadAvailable) });
        })
        .catch(() => {
          if (!cancelled) setDepartureKit(null);
        });
    };
    refreshKit();
    // Export finishes async; keep checking briefly so Download appears without a hard refresh.
    const timer = window.setInterval(refreshKit, 3000);
    const stop = window.setTimeout(() => window.clearInterval(timer), 90_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.clearTimeout(stop);
    };
  }, [
    api,
    projectId,
    section,
    truth?.approval?.approvalId,
    truth?.deliveryDueAt,
    truth?.latestRun?.status,
  ]);

  const confirmationDescription = candidateConfirmationDescription({
    draftDescription: truth?.draft?.description,
    intakeDescription: truth?.intake?.description,
    changeRequestMessage: truth?.changeRequest?.message,
  });
  const businessName =
    truth?.businessName || pipeline.find((row) => row.projectId === projectId)?.businessName || "This business";
  const orientation = projectOrientation({
    businessName,
    projectLabel: "Website project",
    section: projectRoomTabLabel(section as never),
  });
  const versions = versionTruthFromRecords({
    workingVersionId: truth?.versions.working,
    clientVisibleVersionId: truth?.versions.clientVisible,
    approvedVersionId: truth?.versions.approved,
    liveVersionId: truth?.versions.live,
  });
  const approval = approvalCopyFromRecord({
    approvalId: truth?.approval?.approvalId,
    approvedAt: truth?.approval?.approvedAt,
    previewVersionId: approvalPreviewVersionId({
      previewUrl: truth?.previewUrl,
      clientVisibleVersionId: truth?.versions.clientVisible,
    }),
  });
  const changes = truth?.changeRequest
    ? {
        before: `Change request ${truth.changeRequest.changeRequestId} is ${truth.changeRequest.status.toLowerCase()}.`,
        after: truth.changeRequest.status === "RESOLVED"
          ? "A revised preview is bound to this request."
          : "No revised preview is bound yet.",
      }
    : changesEmptyCopy();

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <h1 className="ws-display">{orientation.title}</h1>
      <p className="ws-muted">{orientation.context}</p>
      {loadError ? <FailureGrammar title="Project data is unavailable" detail={loadError} onRetry={() => void reloadTruth()} /> : null}
      <nav aria-label="Project room tabs">
        <ul style={{ display: "flex", gap: 8, listStyle: "none", padding: 0, margin: 0, flexWrap: "wrap" }}>
          {projectRoomTabs().map((tab) => (
            <li key={tab}>
              <button
                type="button"
                className={section === tab ? "ws-btn ws-btn-primary" : "ws-btn"}
                aria-current={section === tab ? "page" : undefined}
                onClick={() => onSection(tab)}
              >
                {projectRoomTabLabel(tab)}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      {actionError ? <FailureGrammar title="That action did not complete" detail={actionError} /> : null}
      {section === "overview" ? (
        <section className="ws-card">
          <h2 className="ws-title">Client access</h2>
          <p>
            State: <strong>{clientAccessLabel({ exists: Boolean(truth?.clientAccess.exists), status: truth?.clientAccess.status })}</strong>
          </p>
          {shouldOfferCreateClientLink(Boolean(truth?.clientAccess.exists)) ||
          shouldOfferReissueClientLink(Boolean(truth?.clientAccess.exists)) ? (
            <button
              type="button"
              className="ws-btn"
              onClick={async () => {
                setActionError(null);
                const result = await createClientAccessOnce({
                  api,
                  projectId,
                  config,
                  inFlight: linkCreateInFlight,
                });
                if (!result.ok) {
                  setActionError(result.error);
                  return;
                }
                if (result.linkUrl) {
                  onFreshLink(result.linkUrl);
                  onToast(
                    result.reissued
                      ? "New copy-link ready. The previous token no longer works. Email send is not available."
                      : "Copy the client access link. Email send is not available.",
                  );
                }
                await reloadTruth();
                onReloadLists();
              }}
            >
              {clientLinkActionLabel(Boolean(truth?.clientAccess.exists))}
            </button>
          ) : null}
          {freshLinkUrl ? (
            <div className="ws-field">
              <label htmlFor="client-link">Client access link</label>
              <input id="client-link" readOnly value={freshLinkUrl} />
            </div>
          ) : truth?.clientAccess.exists ? (
            <p className="ws-muted">
              The secret token is not shown again after refresh. Use Create new copy-link access to mint a fresh link
              for this project.
            </p>
          ) : null}
          <p className="ws-muted">Send and email are not available.</p>
        </section>
      ) : null}
      {section === "business" ? (
        <section className="ws-card">
          <h2 className="ws-title">Candidate review</h2>
          {truth?.intake?.submitted ? (
            <>
              <p>Submitted client intake for {truth.intake.businessName ?? businessName}.</p>
              <ul>
                {truth.intake.primaryPhone ? <li>Phone: {truth.intake.primaryPhone}</li> : <li>Phone is missing.</li>}
                {truth.intake.primaryEmail ? <li>Email: {truth.intake.primaryEmail}</li> : <li>Email is missing.</li>}
                {confirmationDescription ? <li>Description: {confirmationDescription}</li> : null}
              </ul>
              {!truth.draft ? (
                <div className="ws-field">
                  <label htmlFor="candidate-site-lens">Website design</label>
                  <select
                    id="candidate-site-lens"
                    value={selectedLensId}
                    onChange={(event) => setSelectedLensId(event.target.value as typeof selectedLensId)}
                  >
                    <option value="lens-flat-2">Flat 2.0</option>
                    <option value="lens-glass">Glass</option>
                  </select>
                  <p className="ws-muted">Saved to the initial candidate on confirmation.</p>
                </div>
              ) : null}
              {truth.confirmationId && !draftNeedsReconfirmation(truth.draft) ? (
                <GlobalNotice>Candidate confirmed. The website is not active yet.</GlobalNotice>
              ) : (
                <button
                  type="button"
                  className="ws-btn ws-btn-primary"
                  onClick={async () => {
                    setActionError(null);
                    const result = await confirmCandidateOnce({
                      api,
                      agencyId,
                      projectId,
                      selectedLensId,
                      inFlight: confirmInFlight,
                    });
                    if (!result.ok) {
                      setActionError(result.error);
                      return;
                    }
                    await reloadTruth();
                    onReloadLists();
                  }}
                >
                  Confirm candidate
                </button>
              )}
            </>
          ) : (
            <p>No submitted client intake yet. Confirm is closed until the client submits.</p>
          )}
        </section>
      ) : null}
      {section === "website" ? (
        <section className="ws-card">
          <h2 className="ws-title">Website and quality gate</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <ExactVersionChip role="Working" versionLabel={versions.working} />
            <ExactVersionChip role="Client-visible" versionLabel={versions.clientVisible} />
            <ExactVersionChip role="Approved" versionLabel={versions.approved} />
            <ExactVersionChip role="Live" versionLabel={versions.live} />
          </div>
          {truth?.latestRun ? (
            <p>Latest build: {truth.latestRun.status} ({truth.latestRun.runId}).</p>
          ) : (
            <p>No website build has been queued.</p>
          )}
          {truth?.confirmationId && !truth.startBuildBlocker ? (
            <button
              type="button"
              className="ws-btn"
              onClick={async () => {
                setActionError(null);
                const result = await startConfirmedBuildOnce({
                  api,
                  projectId,
                  inFlight: buildInFlight,
                });
                if (!result.ok) {
                  setActionError(result.error);
                  return;
                }
                await reloadTruth();
                onReloadLists();
              }}
            >
              Start build
            </button>
          ) : (
            <p>
              {truth?.startBuildBlocker ??
                "Start build is closed until the candidate is confirmed and eligible."}
            </p>
          )}
          {truth?.previewUrl || truth?.latestRun ? (
            <button
              type="button"
              className="ws-btn"
              disabled={previewLoading}
              onClick={async () => {
                setActionError(null);
                setPreviewLoading(true);
                const result = await loadGreenfieldProjectPreview({ api, projectId, truth });
                setPreviewLoading(false);
                if (!result.ok) {
                  setActionError(result.error);
                  return;
                }
                setInlinePreviewHtml(result.previewHtml);
                setInlinePreviewRunId(result.runId);
                onToast("Preview loaded on this page.");
              }}
            >
              {previewLoading ? "Loading preview…" : "Open preview"}
            </button>
          ) : null}
          {inlinePreviewHtml ? (
            <section className="ws-card" aria-label="Website preview" style={{ marginTop: 12 }}>
              <h3 className="ws-title">
                Website preview
                {inlinePreviewRunId ? ` · ${inlinePreviewRunId}` : ""}
              </h3>
              <p className="ws-muted">Shown in Studio (no new browser tab).</p>
              <iframe
                title="Website preview"
                srcDoc={inlinePreviewHtml}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                style={{
                  width: "100%",
                  minHeight: 520,
                  border: "1px solid var(--ws-border, #c9d0d4)",
                  borderRadius: 8,
                  background: "#fff",
                }}
              />
            </section>
          ) : null}
          <button type="button" className="ws-btn ws-btn-primary" onClick={onWorkbench}>
            Open website workbench
          </button>
        </section>
      ) : null}
      {section === "changes" ? (
        <section className="ws-card">
          <h2 className="ws-title">Structured changes</h2>
          <ChangeDiff before={changes.before} after={changes.after} />
          {truth?.changeRequest?.status === "SUBMITTED" ? (
            <button
              type="button"
              className="ws-btn ws-btn-primary"
              onClick={async () => {
                setActionError(null);
                const result = await markChangeRequestInProgressOnce({
                  api,
                  projectId,
                  inFlight: changeInFlight,
                });
                if (!result.ok) {
                  setActionError(result.error);
                  return;
                }
                onToast("Change request is in progress. Use the requested text as reference, edit the business description, then confirm and rebuild.");
                await reloadTruth();
                onReloadLists();
              }}
            >
              Mark change in progress
            </button>
          ) : null}
          {truth?.changeRequest?.status === "IN_PROGRESS" ? (
            <>
              <p className="ws-muted">Client change request (reference only)</p>
              {truth.changeRequest.message ? (
                <p>{truth.changeRequest.message}</p>
              ) : (
                <p className="ws-muted">This change request has no requested text.</p>
              )}
              <div className="ws-field">
                <label htmlFor="agency-business-description">Business description</label>
                <textarea
                  id="agency-business-description"
                  value={businessDescription}
                  maxLength={2000}
                  rows={4}
                  onChange={(event) => setBusinessDescription(event.target.value)}
                />
              </div>
              <p className="ws-muted">
                Edit and save the business description, confirm the updated candidate, rebuild, then bind
                the revised preview here.
              </p>
              <button
                type="button"
                className="ws-btn ws-btn-primary"
                onClick={async () => {
                  setActionError(null);
                  const result = await saveBusinessDescriptionDraftOnce({
                    api,
                    projectId,
                    description: businessDescription,
                    inFlight: changeInFlight,
                  });
                  if (!result.ok) {
                    setActionError(result.error);
                    return;
                  }
                  onToast("Business description saved. Confirm the updated candidate before rebuilding.");
                  await reloadTruth();
                  onReloadLists();
                }}
              >
                Save business description
              </button>
              <button type="button" className="ws-btn" onClick={() => onSection("business")}>
                Open Business to confirm
              </button>
              <button type="button" className="ws-btn" onClick={() => onSection("website")}>
                Open Website to rebuild
              </button>
              <button
                type="button"
                className="ws-btn ws-btn-primary"
                onClick={async () => {
                  setActionError(null);
                  const result = await resolveChangeRequestWithLatestPreviewOnce({
                    api,
                    projectId,
                    inFlight: changeInFlight,
                  });
                  if (!result.ok) {
                    setActionError(result.error);
                    return;
                  }
                  onToast("Revised preview bound to this change request.");
                  await reloadTruth();
                  onReloadLists();
                }}
              >
                Bind revised preview
              </button>
            </>
          ) : null}
        </section>
      ) : null}
      {section === "approval-delivery" ? (
        <section className="ws-card">
          <h2 className="ws-title">Approval & Delivery</h2>
          <p>
            Approval state: {approval.showCondition ? <Condition condition={approval.condition} /> : null}{" "}
            {approval.label}
          </p>
          <p>{approval.detail}</p>
          {approval.condition === "WAITING" ? (
            <p className="ws-muted">
              The client must approve this exact preview in Client Hub. Studio cannot record exact approval.
            </p>
          ) : null}
          {truth?.deliveryDueAt ? <p>Delivery due: {truth.deliveryDueAt}.</p> : null}
          {shouldOfferPrepareDelivery({
            approvalComplete: approval.condition === "COMPLETE",
            deliveryDueAt: truth?.deliveryDueAt,
          }) ? (
            <button
              type="button"
              className="ws-btn ws-btn-primary"
              onClick={async () => {
                setActionError(null);
                const result = await prepareDeliveryOnce({
                  api,
                  projectId,
                  inFlight: deliveryInFlight,
                });
                if (!result.ok) {
                  setActionError(result.error);
                  return;
                }
                onToast("Delivery prepared. Package handoff is available next.");
                await reloadTruth();
                onReloadLists();
              }}
            >
              Prepare delivery
            </button>
          ) : null}
          {shouldOfferPackageHandoff({
            approvalComplete: approval.condition === "COMPLETE",
            deliveryDueAt: truth?.deliveryDueAt,
            runId: truth?.approval?.runId ?? truth?.latestRun?.runId,
          }) ? (
            <button
              type="button"
              className="ws-btn ws-btn-primary"
              onClick={async () => {
                setActionError(null);
                const result = await packageHandoffOnce({
                  api,
                  projectId,
                  inFlight: deliveryInFlight,
                });
                if (!result.ok) {
                  setActionError(result.error);
                  return;
                }
                onToast("Packaging handoff…");
                await reloadTruth();
                onReloadLists();
                let ready = false;
                for (let attempt = 0; attempt < 20; attempt += 1) {
                  try {
                    const kit = await api.getDepartureKitStatus(projectId);
                    if (kit.downloadAvailable) {
                      setDepartureKit({ downloadAvailable: true });
                      onToast("Departure Kit is ready to download.");
                      ready = true;
                      break;
                    }
                    setDepartureKit({ downloadAvailable: false });
                  } catch {
                    setDepartureKit(null);
                  }
                  await new Promise((resolve) => window.setTimeout(resolve, 1500));
                }
                if (!ready) {
                  onToast("Packaging is still running. Stay on Approval & Delivery — Download appears when ready.");
                }
              }}
            >
              Package handoff
            </button>
          ) : null}
          {departureKit?.downloadAvailable ? (
            <button
              type="button"
              className="ws-btn"
              onClick={async () => {
                setActionError(null);
                try {
                  const blob = await api.downloadDepartureKit(projectId);
                  const url = URL.createObjectURL(blob);
                  const anchor = document.createElement("a");
                  anchor.href = url;
                  anchor.download = `departure-kit-${projectId}.zip`;
                  anchor.click();
                  URL.revokeObjectURL(url);
                  onToast("Departure Kit download started.");
                } catch (error) {
                  setActionError(error instanceof Error ? error.message : "departure_kit_download_failed");
                }
              }}
            >
              Download Departure Kit
            </button>
          ) : null}
          {approval.condition === "COMPLETE" && truth?.deliveryDueAt ? (
            <div style={{ marginTop: "1rem" }} data-testid="greenfield-ownership-panel">
              <WebsiteOwnershipRecordPanel api={api} projectId={projectId} />
            </div>
          ) : (
            <p className="ws-muted">
              Delivery and ownership records appear only after exact approval and delivery preparation exist.
            </p>
          )}
        </section>
      ) : null}
      {section === "notes" ? (
        <section className="ws-card">
          <h2 className="ws-title">Notes</h2>
          {notes.length === 0 ? <p className="ws-muted">No notes yet.</p> : null}
          <ul>
            {notes.map((note) => (
              <li key={note.noteId ?? note.body}>{note.body}</li>
            ))}
          </ul>
          <div className="ws-field">
            <label htmlFor="project-note">Internal note</label>
            <textarea id="project-note" value={noteBody} onChange={(event) => setNoteBody(event.target.value)} />
          </div>
          <button
            type="button"
            className="ws-btn"
            onClick={async () => {
              if (!noteBody.trim()) return;
              await api.createProjectNote(projectId, { noteType: "INTERNAL", body: noteBody.trim() }, newIdempotencyKey("gf-note"));
              setNoteBody("");
              const row = await api.listProjectNotes(projectId);
              setNotes([...(row.notes ?? [])]);
            }}
          >
            Save note
          </button>
        </section>
      ) : null}
      {section === "activity" ? (
        <section className="ws-card">
          <h2 className="ws-title">Activity</h2>
          {activity.length === 0 ? <p className="ws-muted">No project activity yet.</p> : null}
          <ul>
            {activity.map((item, index) => (
              <li key={`${item.occurredAt ?? "a"}-${index}`}>
                {item.payloadSummary || item.eventType} {item.occurredAt ? `· ${item.occurredAt}` : ""}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {section === "advanced" ? (
        <section className="ws-card">
          <h2 className="ws-title">Advanced</h2>
          <p className="ws-muted">Technical identifiers stay off the normal path.</p>
          <p>Project id: {projectId}</p>
          {truth?.clientAccess.clientLinkId ? <p>Client access id: {truth.clientAccess.clientLinkId}</p> : null}
        </section>
      ) : null}
    </div>
  );
}

function WorkbenchView({
  api,
  projectId,
  pipeline,
  pollIntervalMs,
  onBack,
}: {
  readonly api: StudioApiClient;
  readonly projectId: string;
  readonly pipeline: ReadonlyArray<{ projectId: string; businessName?: string | null }>;
  readonly pollIntervalMs: number;
  readonly onBack: () => void;
}) {
  const loadTruth = useCallback(() => api.getStep5RoomTruth(projectId), [api, projectId]);
  const polled = usePolling(loadTruth, pollIntervalMs);
  const truth = polled.data;
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [inlinePreviewHtml, setInlinePreviewHtml] = useState<string | null>(null);
  const [inlinePreviewRunId, setInlinePreviewRunId] = useState<string | null>(null);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") polled.refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [polled.refresh]);
  const versions = versionTruthFromRecords({
    workingVersionId: truth?.versions.working,
    clientVisibleVersionId: truth?.versions.clientVisible,
    approvedVersionId: truth?.versions.approved,
    liveVersionId: truth?.versions.live,
  });
  const businessName =
    truth?.businessName || pipeline.find((row) => row.projectId === projectId)?.businessName || "This business";
  const notice = workbenchPreviewNotice({
    previewUrl: truth?.previewUrl,
    workingVersionLabel: versions.working,
    latestRunStatus: truth?.latestRun?.status,
  });
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <button type="button" className="ws-btn" onClick={onBack}>
        Back to project room
      </button>
      <h1 className="ws-display">Website workbench</h1>
      <p className="ws-muted">{businessName} · Website project</p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <ExactVersionChip role="Working" versionLabel={versions.working} />
        <ExactVersionChip role="Client-visible" versionLabel={versions.clientVisible} />
        <ExactVersionChip role="Approved" versionLabel={versions.approved} />
        <ExactVersionChip role="Live" versionLabel={versions.live} />
      </div>
      {notice.kind === "available" ? <p>{notice.message}</p> : <GlobalNotice>{notice.message}</GlobalNotice>}
      {previewError ? <FailureGrammar title="Preview did not open" detail={previewError} /> : null}
      <button
        type="button"
        className="ws-btn ws-btn-primary"
        disabled={opening || (!truth?.previewUrl && notice.kind === "waiting")}
        onClick={async () => {
          setOpening(true);
          setPreviewError(null);
          const result = await loadGreenfieldProjectPreview({ api, projectId, truth });
          setOpening(false);
          if (!result.ok) {
            setPreviewError(result.error);
            return;
          }
          setInlinePreviewHtml(result.previewHtml);
          setInlinePreviewRunId(result.runId);
        }}
      >
        {opening ? "Loading preview…" : "Open preview"}
      </button>
      {inlinePreviewHtml ? (
        <section className="ws-card" aria-label="Website preview">
          <h2 className="ws-title">
            Website preview
            {inlinePreviewRunId ? ` · ${inlinePreviewRunId}` : ""}
          </h2>
          <p className="ws-muted">Shown in Studio (no new browser tab).</p>
          <iframe
            title="Website preview"
            srcDoc={inlinePreviewHtml}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            style={{
              width: "100%",
              minHeight: 520,
              border: "1px solid var(--ws-border, #c9d0d4)",
              borderRadius: 8,
              background: "#fff",
            }}
          />
        </section>
      ) : null}
    </div>
  );
}

function SettingsView({ onLogout }: { readonly onLogout?: () => Promise<void> | void }) {
  const [busy, setBusy] = useState(false);
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <h1 className="ws-display">Settings</h1>
      <section className="ws-card">
        <h2 className="ws-title">Roles</h2>
        <p>Owner, admin, and operator roles come from the agency session. This screen does not invent access.</p>
      </section>
      <section className="ws-card">
        <h2 className="ws-title">Integrations</h2>
        <p>Recorded or deferred only. Provider-verified is not available.</p>
      </section>
      <section className="ws-card">
        <h2 className="ws-title">Session</h2>
        <button
          type="button"
          className="ws-btn"
          disabled={busy || !onLogout}
          onClick={async () => {
            if (!onLogout) return;
            setBusy(true);
            await onLogout();
          }}
        >
          Log out
        </button>
      </section>
    </div>
  );
}
